import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { deactivateTenant, deleteTenant } from '../../api/client';
import {
  INACTIVE_REASONS,
  PURGE_MIN_GAP_DAYS,
  describeDeletion,
  isInactiveStatus,
  nextFreePurgeDate,
  purgeDateBounds,
} from '../../utils/tenantLifecycle';

// One dialog for all three lifecycle actions (2026-09-29):
//   - deactivate an active/pending tenant (choose paused / retained / pending deletion),
//   - change the state of an inactive tenant (e.g. retained → pending deletion, postpone or cancel a purge),
//   - schedule a deletion (opens preset to pending deletion).
// The server (TenantLifecycle.psm1) re-validates everything; the date rules here only mirror it.
// `canDelete` gates the pending-deletion option: scheduling a deletion is global-admin-only server-side.
// `takenDates` are purge dates other tenants' LOG deletions already hold (takenPurgeDates): the data teams
// delete one tenant per day, so a log deletion must pick a free date. The server re-checks.
export default function InactiveStateModal({ tenant, initialReason, canDelete, takenDates = [], onClose, onDone }) {
  const alreadyInactive = isInactiveStatus(tenant.status);
  const bounds = useMemo(
    () => purgeDateBounds(alreadyInactive ? tenant.deactivatedUtc : null),
    [alreadyInactive, tenant.deactivatedUtc],
  );

  const [reason, setReason] = useState(initialReason || (alreadyInactive ? tenant.inactiveReason : '') || 'retained');
  const [purgeDate, setPurgeDate] = useState(
    tenant.purgeDate && tenant.purgeDate >= bounds.min ? tenant.purgeDate : bounds.defaultDate,
  );
  // No default for the log-data choice: it decides whether a client's logs are deleted, so it is asked, not assumed.
  const [purgeLogs, setPurgeLogs] = useState(
    tenant.purgeLogs === 'TRUE' ? true : tenant.purgeLogs === 'FALSE' ? false : null,
  );
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setError(null); }, [reason, purgeDate, purgeLogs]);

  const name = tenant.friendlyName || tenant.tenantId;
  const isPending = reason === 'pending_deletion';
  const dateTooEarly = isPending && purgeDate < bounds.min;
  const dateTaken = isPending && purgeLogs === true && takenDates.includes(purgeDate);
  const freeDate = dateTaken ? nextFreePurgeDate(purgeDate, takenDates) : null;
  const needsConfirm = isPending;
  const confirmOk = !needsConfirm || confirmText.trim().toLowerCase() === 'delete';
  const canSubmit = !busy && reason
    && (!isPending || (purgeDate && !dateTooEarly && !dateTaken && purgeLogs !== null && confirmOk));

  const title = alreadyInactive
    ? 'Change tenant state'
    : initialReason === 'pending_deletion'
      ? 'Schedule tenant deletion'
      : 'Deactivate tenant';

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isPending) {
        await deleteTenant(tenant.tenantId, { purgeDate, purgeLogs });
      } else {
        await deactivateTenant(tenant.tenantId, { inactiveReason: reason });
      }
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg border border-gray-700 bg-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-100">{title}: {name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200" disabled={busy}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-xs text-gray-400">
            Every state below stops log collection. Log Collection then stops the tenant’s Activity Feed
            subscriptions automatically.
          </p>

          <div className="space-y-2">
            {INACTIVE_REASONS.map((r) => {
              const disabled = r.value === 'pending_deletion' && !canDelete;
              return (
                <label
                  key={r.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                    reason === r.value ? 'border-blue-600/60 bg-blue-900/20' : 'border-gray-700/60 hover:bg-gray-800/40'
                  } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  title={disabled ? 'Scheduling a deletion requires global admin.' : undefined}
                >
                  <input
                    type="radio"
                    name="inactiveReason"
                    className="mt-0.5"
                    checked={reason === r.value}
                    disabled={disabled || busy}
                    onChange={() => setReason(r.value)}
                  />
                  <span>
                    <span className="block text-sm text-gray-200">{r.label}</span>
                    <span className="block text-xs text-gray-500">{r.description}</span>
                    {disabled && <span className="block text-xs text-amber-500/80">Requires global admin.</span>}
                  </span>
                </label>
              );
            })}
          </div>

          {isPending && (
            <div className="space-y-3 rounded-lg border border-red-800/40 bg-red-950/20 p-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-400">Delete on (UTC)</label>
                <input
                  type="date"
                  className="input-field"
                  value={purgeDate}
                  min={bounds.min}
                  onChange={(e) => setPurgeDate(e.target.value)}
                  disabled={busy}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Earliest {bounds.min}: at least {PURGE_MIN_GAP_DAYS} days after deactivation, so collection
                  can finish stopping and the last staged logs can drain first.
                </p>
                {dateTooEarly && <p className="mt-1 text-xs text-red-400">Choose {bounds.min} or later.</p>}
                {dateTaken && (
                  <p className="mt-1 text-xs text-red-400">
                    Another tenant’s log data is already due for deletion on {purgeDate}, and the data teams
                    delete one tenant per day.{' '}
                    <button type="button" className="underline hover:text-red-300" onClick={() => setPurgeDate(freeDate)}>
                      Use {freeDate}
                    </button>
                  </p>
                )}
              </div>

              <fieldset>
                <legend className="mb-1 text-xs font-medium text-gray-400">What gets deleted?</legend>
                <label className="flex items-start gap-2 py-1 text-sm text-gray-200">
                  <input type="radio" name="purgeLogs" className="mt-1" checked={purgeLogs === true}
                    onChange={() => setPurgeLogs(true)} disabled={busy} />
                  <span>The tenant <b>and its log data</b></span>
                </label>
                <label className="flex items-start gap-2 py-1 text-sm text-gray-200">
                  <input type="radio" name="purgeLogs" className="mt-1" checked={purgeLogs === false}
                    onChange={() => setPurgeLogs(false)} disabled={busy} />
                  <span>The tenant’s portal records only (keep the log data)</span>
                </label>
                {purgeLogs !== null && <p className="mt-1 text-xs text-gray-400">{describeDeletion(purgeLogs)}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  To keep the logs <i>and</i> keep the tenant available for investigations, choose Retained instead.
                </p>
              </fieldset>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-400">Type <b>delete</b> to confirm</label>
                <input className="input-field" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  disabled={busy} />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-700/50 bg-red-900/30 p-2">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-800 px-5 py-3">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className={isPending ? 'btn-danger flex items-center gap-2' : 'btn-primary flex items-center gap-2'}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {busy && <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-current" />}
            {isPending ? 'Schedule deletion' : alreadyInactive ? 'Save state' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}
